// ============================================================================
// API / Backend Constants
// ============================================================================

// Assessment task types
export const ASSESSMENT_TASKS = {
  products: 'list_of_products',
  companyFull: 'company_full', // runs info, company_info, list_of_products, ai_assessment_qa
  companyInfo: 'company_ai_assessment', // runs info, company_info, list_of_products
  productFull: 'product_full', // runs info, product_info, ai_assessment_qa
  productInfo: 'product_ai_assessment', // runs info, product_info
}

// Assessment status types
export const ASSESSMENT_STATUS = {
  DONE: 'done',
  IN_PROGRESS: 'running',
  ERROR: 'error',
  NOT_STARTED: "hasn't started",
}

// AI task card content by variant
export const AI_TASK_CARD_CONTENT = {
  info: {
    title: 'Gathering Company Information',
    message: 'Our AI is gathering information about this company.',
    subMessage: 'This might take a few minutes, please come back later.',
  },
  assessment: {
    title: 'AI Research in Progress',
    message: 'Our AI is creating an assessment for this company.',
    subMessage: 'This might take a few minutes, please come back later.',
  },
  subprocessorFetch: {
    title: 'Fetching Subprocessor List',
    message: 'We are fetching and processing the subprocessor list for this company.',
    subMessage: 'This may take a few minutes. You can leave this page and come back later.',
  },
}

// Company section mapping for API
export const companySectionMap = {
  info: 'company_info',
  parent: 'company_info',
  contact: 'contact_info',
  dpf: 'dpf_info',
  aiAssessment: 'company_ai_assessment_qa',
  product: 'product_info',
  productsAssessment: 'product_ai_assessment_qa',
}

// ============================================================================
// UI / Frontend Constants
// ============================================================================

// Create Modal Steps
export const CREATE_MODAL_STEPS = {
  INPUT: 'input',
  LOADING: 'loading',
  RESULTS: 'results',
}

export const CREATE_MODAL_START_SUBPROCESSOR_LIST_LABEL = 'Start subprocessor list fetch'

export const initialCreateModalFormData = {
  entityName: '',
  fullAiAssessment: false,
  startSubprocessorList: true,
  currentStep: CREATE_MODAL_STEPS.INPUT,
  selectedOption: null, // 'ai' | 'custom' | 'existing' | null
  selectedExistingCompanyId: null, // string | null (set only when selectedOption === 'existing')
}

// Company Details Tabs
export const COMPANY_DETAILS_TABS = {
  INFO: 'info',
  CONTACT: 'contact',
  DPF: 'dpf',
  AI_ASSESSMENT: 'ai-assessment',
  PRODUCTS: 'products',
  DOCUMENTS: 'documents',
  SUBPROCESSOR_MAP: 'subprocessor-map',
  SUBPROCESSOR_LIST: 'subprocessor-list',
  SUBPROCESSOR_REVERSE: 'used-as-subprocessor',
}

/** Soft character limit for a single company note. */
export const COMPANY_NOTE_MAX_LENGTH = 2000

// Company Details Nav
export function getCompanyDetailsTabs() {
  return [
    {
      name: COMPANY_DETAILS_TABS.INFO,
      label: 'Company information',
      icon: 'info',
      field: 'info',
    },
    {
      name: COMPANY_DETAILS_TABS.CONTACT,
      label: 'Contact information',
      icon: 'contact_page',
      field: 'contact',
    },
    {
      name: COMPANY_DETAILS_TABS.DPF,
      label: 'Data protection framework',
      icon: 'data_object',
      field: 'dpf',
    },
    {
      name: COMPANY_DETAILS_TABS.AI_ASSESSMENT,
      label: 'Company AI Assessment',
      icon: 'analytics',
      field: 'aiAssessment',
    },
    {
      name: COMPANY_DETAILS_TABS.PRODUCTS,
      label: 'Products',
      icon: 'inventory_2',
      field: 'products',
    },
    {
      name: COMPANY_DETAILS_TABS.DOCUMENTS,
      label: 'Documents',
      icon: 'folder',
      field: null,
    },
    {
      name: COMPANY_DETAILS_TABS.SUBPROCESSOR_LIST,
      label: 'Subprocessor list',
      icon: 'list_alt',
      field: null,
    },
    {
      name: COMPANY_DETAILS_TABS.SUBPROCESSOR_REVERSE,
      label: 'Used as a subprocessor',
      icon: 'call_received',
      field: null,
    },
    {
      name: COMPANY_DETAILS_TABS.SUBPROCESSOR_MAP,
      label: 'Subprocessor map',
      icon: 'hub',
      field: null,
    },
  ]
}

// Company Edit Steps
export const COMPANY_EDIT_STEPS = {
  INFO: 'info',
  PARENT: 'parent',
  CONTACT: 'contact',
  AI_ASSESSMENT: 'ai-assessment',
  PRODUCTS: 'products',
  SUBPROCESSORS: 'subprocessors',
}

// Company Edit Nav
export function getCompanyEditSteps() {
  return [
    {
      title: 'Company Information',
      icon: 'info',
      name: COMPANY_EDIT_STEPS.INFO,
      cmpName: 'CompanyEditInfo',
    },
    {
      title: 'Parent Company',
      icon: 'account_tree',
      name: COMPANY_EDIT_STEPS.PARENT,
      cmpName: 'CompanyEditParent',
    },
    {
      title: 'Contact Information',
      icon: 'account_circle',
      name: COMPANY_EDIT_STEPS.CONTACT,
      cmpName: 'CompanyEditContact',
    },
    {
      title: 'Company Assessment',
      icon: 'analytics',
      name: COMPANY_EDIT_STEPS.AI_ASSESSMENT,
      cmpName: 'CompanyEditAiAssessment',
    },
    {
      title: 'Products',
      icon: 'inventory_2',
      name: COMPANY_EDIT_STEPS.PRODUCTS,
      cmpName: 'CompanyEditProducts',
    },
    {
      title: 'Subprocessors',
      icon: 'hub',
      name: COMPANY_EDIT_STEPS.SUBPROCESSORS,
      cmpName: 'CompanyEditSubprocessors',
    },
  ]
}

// ============================================================================
// Form Initial Data
// ============================================================================

export const initialInfoData = {
  name: '',
  legalName: '',
  website: '',
  active: false,
  providesAiServices: null,
  isPublicCompany: false,
  stockExchangeName: '',
  tickerSymbol: '',
  numberOfEmployees: '',
}

export const initialContactData = {
  name: '',
  title: '',
  email: '',
  phoneNumber: '',
  breachNotificationEmail: '',
}

export const initialAiAssessmentData = {
  sections: [],
}

export const initialProductData = {
  name: '',
  usesAi: false,
  active: true,
  riskLevel: 'minimal',
  sections: [],
}

export const initialAddressData = {
  addressTitle: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  state: '',
  country: '',
  makeDefault: false,
}

// ============================================================================
// AI Assessment Templates
// ============================================================================

export const initialCompanyAiAssessmentTemplate = {
  assessmentTypeToUse: '',
  createdByAi: false,
  createdOn: '',
  isTemplate: true,
  sections: [
    {
      sectionId: 'ai_product_provider',
      title: 'Who is providing the AI product?',
      questions: [
        {
          questionId: 'company_legal_name',
          questionTitle: 'Company Full Name',
          aiQuestion: "What is the company's full legal name?",
          aiAnswer: '',
          sectionId: 'ai_product_provider',
        },
        {
          questionId: 'company_trade_name',
          questionTitle: 'Usually known as',
          aiQuestion: "What is the company's trade name (or 'doing business as')?",
          aiAnswer: '',
          sectionId: 'ai_product_provider',
        },
        {
          questionId: 'company_parent_or_group_company',
          questionTitle: 'Affiliates',
          aiQuestion: 'Is the company part of a group? If yes, what is the name of the group?',
          aiAnswer: '',
          sectionId: 'ai_product_provider',
        },
      ],
    },
    {
      sectionId: 'company_found',
      title: 'Where is the Company to be found?',
      questions: [
        {
          questionId: 'website_url',
          questionTitle: 'Company Website',
          aiQuestion: 'What is the main website of the company?',
          aiAnswer: '',
          sectionId: 'company_found',
        },
        {
          questionId: 'company_jurisdiction',
          questionTitle: 'Primary place of Business',
          aiQuestion:
            'In which jurisdiction is the company located? Please provide the main jurisdiction where the company does business.',
          aiAnswer: '',
          sectionId: 'company_found',
        },
        {
          questionId: 'company_operating_jurisdictions',
          questionTitle: 'Jurisdictions where company services are offered',
          aiQuestion:
            'In which jurisdictions does the company operate? Please provide a list of all jurisdictions where the company has operations and/or where it stores data.',
          aiAnswer: '',
          sectionId: 'company_found',
        },
      ],
    },
    {
      sectionId: 'company_safe',
      title: 'Is Company considered safe and secure?',
      questions: [
        {
          questionId: 'company_reputable_service_provider',
          questionTitle: 'Service Provider',
          aiQuestion:
            'Is the company considered to be a reputable service provider? Please give details.',
          aiAnswer: '',
          sectionId: 'company_safe',
        },
        {
          questionId: 'company_security_certifications',
          questionTitle: 'Security Certifications',
          aiQuestion:
            'Please tell me whether the company maintains certifications such as SOC2 or ISO27001? Please give details.',
          aiAnswer: '',
          sectionId: 'company_safe',
        },
        {
          questionId: 'company_data_retention_policy',
          questionTitle: 'Retention Policy',
          aiQuestion:
            'Does the company have a retention policy in place? If yes, is it publicly available? Please give details.',
          aiAnswer: '',
          sectionId: 'company_safe',
        },
      ],
    },
    {
      sectionId: 'dispute_resolution',
      title: 'Dispute Resolution',
      questions: [
        {
          questionId: 'company_dispute_resolution_jurisdiction',
          questionTitle: 'Jurisdiction',
          aiQuestion:
            'Do the commercial terms of the company include an applicable jurisdiction for dispute resolution? If yes, which jurisdiction? Please give details.',
          aiAnswer: '',
          sectionId: 'dispute_resolution',
        },
        {
          questionId: 'company_jurisdiction_applicable_law',
          questionTitle: 'Applicable Law',
          aiQuestion:
            'Do the commercial terms of the company include an applicable law? If yes, the laws of which jurisdiction? Please give details.',
          aiAnswer: '',
          sectionId: 'dispute_resolution',
        },
      ],
    },
  ],
}

export const initialProductAiAssessmentTemplate = {
  isTemplate: true,
  sections: [
    {
      sectionId: 'primary_ai_function',
      title: 'Primary Function of AI product and risk profile under the EU AI Act',
      questions: [
        {
          questionId: 'product_purpose_and_use',
          questionTitle: 'product purpose',
          aiQuestion:
            'What is the purpose of the product? What is the classic use case for the product?',
          aiAnswer: '',
          sectionId: 'primary_ai_function',
        },
        {
          questionId: 'product_category_risk',
          questionTitle: 'product category risk',
          aiQuestion:
            'How would the product be categorized according to the EU AI Act? Would the risk be categorized as unacceptable, high, limited or minimal?',
          aiAnswer: '',
          sectionId: 'primary_ai_function',
        },
        {
          questionId: 'product_why_necessary_or_helpful',
          questionTitle: 'product why necessary or helpful',
          aiQuestion:
            'Give details of what problem can be resolved or what can be improved when using the product?',
          aiAnswer: '',
          sectionId: 'primary_ai_function',
        },
        {
          questionId: 'product_how_is_improvement',
          questionTitle: 'product how is improvement',
          aiQuestion:
            'How is the product an improvement on other methods which are not based on AI? For example: Less time, more cost effective, following predetermined rules without deviation, preventing human fatigue.',
          aiAnswer: '',
          sectionId: 'primary_ai_function',
        },
      ],
    },
    {
      sectionId: 'data_categories',
      title: 'Categories of Data processed and how the data is used',
      questions: [
        {
          questionId: 'product_types_data_collected_and_processed',
          questionTitle: 'product types data collected and processed',
          aiQuestion:
            'Please explain what categories of data are usually collected in order for the product services to be offered? For example, voice recordings or IP addresses. Then explain how the collected data is used to provide the product services.',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
        {
          questionId: 'product_types_data_product_outputs',
          questionTitle: 'product types data product outputs',
          aiQuestion: 'Explain what is the output that the product typically produces.',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
        {
          questionId: 'product_sources_of_data',
          questionTitle: 'product sources of data',
          aiQuestion:
            'What is the source of data that the product uses? Is it from publicly available sources?',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
        {
          questionId: 'product_build_profiles_with_personal_info',
          questionTitle: 'product build profiles with personal info',
          aiQuestion:
            'Please comment on whether or not the product builds profiles of humans based on personal information.',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
        {
          questionId: 'product_make_autonomous_decisions',
          questionTitle: 'product make autonomous decisions',
          aiQuestion:
            'Please comment on whether or not the product makes autonomous decisions on behalf of its clients.',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
        {
          questionId: 'product_make_predictions',
          questionTitle: 'product make predictions',
          aiQuestion:
            'Please comment on whether or not the product makes predictions on behalf of its clients.',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
        {
          questionId: 'product_collect_sensitive_personal_data',
          questionTitle: 'product collect sensitive personal data',
          aiQuestion:
            'Please comment on whether or not the product collects any sensitive personal data as defined under GDPR.',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
        {
          questionId: 'product_collect_sensitive_commercial_data',
          questionTitle: 'product collect sensitive commercial data',
          aiQuestion: 'Does the product collect any commercial data of a sensitive nature?',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
        {
          questionId: 'product_retain_data_collected',
          questionTitle: 'product retain data collected',
          aiQuestion:
            'Please comment on whether or not the company retains the data collected as part of providing the product.',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
        {
          questionId: 'product_use_collected_data_to_train_ai',
          questionTitle: 'product use collected data to train ai',
          aiQuestion:
            'Please comment on whether or not the company uses the data collected as part of providing the product in order to train its machine learning models.',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
        {
          questionId: 'product_consent_obtained_use_personal_data',
          questionTitle: 'product consent obtained use personal data',
          aiQuestion:
            'Please comment on whether or not the product requires the use of personal data. If the product does require the use of personal data, please explain whether or not consents are obtained as necessary under applicable data protection law. Please give details.',
          aiAnswer: '',
          sectionId: 'data_categories',
        },
      ],
    },
    {
      sectionId: 'how_ai_works',
      title: 'How the AI Product works',
      questions: [
        {
          questionId: 'product_how_makes_decisions_or_predictions',
          questionTitle: 'product how makes decisions or predictions',
          aiQuestion: 'Describe how the product uses AI to make decisions or predictions.',
          aiAnswer: '',
          sectionId: 'how_ai_works',
        },
        {
          questionId: 'product_way_to_explain_ai_reasoning',
          questionTitle: 'product way to explain ai reasoning',
          aiQuestion: "Is there a way to explain the reasoning behind the product's AI outputs?",
          aiAnswer: '',
          sectionId: 'how_ai_works',
        },
        {
          questionId: 'product_transparency_reports',
          questionTitle: 'product transparency reports',
          aiQuestion:
            'Does the company offer transparency reports or ethical guidelines? Please give details.',
          aiAnswer: '',
          sectionId: 'how_ai_works',
        },
        {
          questionId: 'product_ai_disclosures',
          questionTitle: 'product ai disclosures',
          aiQuestion:
            "Are the AI's algorithms, data sources, and limitations disclosed to users or to customers who chose to deploy the product? Please give details.",
          aiAnswer: '',
          sectionId: 'how_ai_works',
        },
      ],
    },
    {
      sectionId: 'bias_and_fairness',
      title: 'Bias and Fairness within the AI Product',
      questions: [
        {
          questionId: 'product_steps_so_ai_is_free_from_bias',
          questionTitle: 'product steps so ai is free from bias',
          aiQuestion:
            'What steps have been taken to ensure the AI output of the product is free from bias? For example, using facts rather than opinions.',
          aiAnswer: '',
          sectionId: 'bias_and_fairness',
        },
        {
          questionId: 'product_ai_training_data_diverse',
          questionTitle: 'product ai training data diverse',
          aiQuestion:
            'Was the training data for the product diverse and representative of different populations?',
          aiAnswer: '',
          sectionId: 'bias_and_fairness',
        },
        {
          questionId: 'product_how_handles_sensitive_attributes',
          questionTitle: 'product how handles sensitive attributes',
          aiQuestion:
            'Please comment on how the product handles sensitive attributes (e.g., race, gender, age)?',
          aiAnswer: '',
          sectionId: 'bias_and_fairness',
        },
        {
          questionId: 'product_ai_recommendations_audited',
          questionTitle: 'product ai recommendations audited',
          aiQuestion: "Can the AI tool's decisions or recommendations be audited for fairness?",
          aiAnswer: '',
          sectionId: 'bias_and_fairness',
        },
      ],
    },
    {
      sectionId: 'reliability_ai_product',
      title: 'Reliability of AI Product',
      questions: [
        {
          questionId: 'product_testing_for_robustness_and_reliability',
          questionTitle: 'product testing for robustness and reliability',
          aiQuestion:
            'Please comment on whether the product undergoes regular testing for robustness and reliability of its output?',
          aiAnswer: '',
          sectionId: 'reliability_ai_product',
        },
        {
          questionId: 'product_fails_or_is_incorrect',
          questionTitle: 'product fails or is incorrect',
          aiQuestion:
            'Please explain what could be the implications if the product fails or produces incorrect outputs. What would be the likely consequences?',
          aiAnswer: '',
          sectionId: 'reliability_ai_product',
        },
      ],
    },
    {
      sectionId: 'secure_ai_product',
      title: 'How secure is the AI Product',
      questions: [
        {
          questionId: 'product_what_product_vendors',
          questionTitle: 'product what product vendors',
          aiQuestion: 'Who are the vendors that the company uses to provide the product?',
          aiAnswer: '',
          sectionId: 'secure_ai_product',
        },
        {
          questionId: 'product_measures_to_secure_data',
          questionTitle: 'product measures to secure data',
          aiQuestion: 'What measures are in place to secure the data used as part of the product?',
          aiAnswer: '',
          sectionId: 'secure_ai_product',
        },
        {
          questionId: 'product_risks_mitigated',
          questionTitle: 'product risks mitigated',
          aiQuestion:
            'What are the possible risks associated with the product and are they adequately mitigated?',
          aiAnswer: '',
          sectionId: 'secure_ai_product',
        },
      ],
    },
    {
      sectionId: 'accountability_ai_product',
      title: 'Accountability of the AI Product',
      questions: [
        {
          questionId: 'product_point_of_contact_for_concerns',
          questionTitle: 'product point of contact for concerns',
          aiQuestion: 'Who is the point of contact, if someone has concerns about the product?',
          aiAnswer: '',
          sectionId: 'accountability_ai_product',
        },
        {
          questionId: 'product_human_involved_in_ai_reviewing',
          questionTitle: 'product human involved in ai reviewing',
          aiQuestion:
            'Is there a human involved in the provision of the product? In particular, is there a human reviewing the AI output?',
          aiAnswer: '',
          sectionId: 'accountability_ai_product',
        },
        {
          questionId: 'product_who_responsible_causes_harm',
          questionTitle: 'product who responsible causes harm',
          aiQuestion:
            'Who is responsible if the product causes harm or makes an incorrect decision?',
          aiAnswer: '',
          sectionId: 'accountability_ai_product',
        },
        {
          questionId: 'product_is_results_tested',
          questionTitle: 'product is results tested',
          aiQuestion:
            'Are the results of the product tested for accuracy and effectiveness prior to deployment? Please give details.',
          aiAnswer: '',
          sectionId: 'accountability_ai_product',
        },
        {
          questionId: 'product_has_mechanism_to_report_and_resolve',
          questionTitle: 'product has mechanism to report and resolve',
          aiQuestion:
            'Please comment on whether the product has a mechanism to report and resolve errors or unethical behavior?',
          aiAnswer: '',
          sectionId: 'accountability_ai_product',
        },
        {
          questionId: 'product_documented_ethical_ai_policy',
          questionTitle: 'product documented ethical ai policy',
          aiQuestion:
            'Does the company have a documented ethical AI policy or governance framework? Please give details.',
          aiAnswer: '',
          sectionId: 'accountability_ai_product',
        },
        {
          questionId: 'product_does_company_offer_training_on',
          questionTitle: 'product does company offer training on',
          aiQuestion:
            'Please comment on whether the company offers training so users know how and when to use the product.',
          aiAnswer: '',
          sectionId: 'accountability_ai_product',
        },
      ],
    },
  ],
}

// ============================================================================
// Field Definitions
// ============================================================================

// Company Info Fields
export const basicInfoFields = [
  { key: 'name', label: 'Company Name' },
  { key: 'legalName', label: 'Legal Name' },
  { key: 'website', label: 'Website', infoOnly: true },
  { key: 'numberOfEmployees', label: 'Number of Employees', infoOnly: true },
  { key: 'tickerSymbol', label: 'Ticker Symbol', infoOnly: true },
  { key: 'dateAdded', label: 'Date Added', infoOnly: true },
]

export const statusFields = [
  {
    key: 'active',
    label: 'Company Status',
    trueText: 'Active',
    falseText: 'Inactive',
  },
  {
    key: 'providesAiServices',
    label: 'AI Services',
    trueText: 'Provides AI Services',
    falseText: 'Does not provide AI Services',
  },
  {
    key: 'isDpfFound',
    label: 'Data Privacy Framework',
    trueText: 'Found',
    falseText: 'Not found',
    infoOnly: true,
  },
  {
    key: 'isPublicCompany',
    label: 'Public Company',
    trueText: 'Public',
    falseText: 'Private',
    infoOnly: true,
  },
  { key: 'stockExchangeName', label: 'Stock Exchange', infoOnly: true },
]

export const contactFields = {
  contactInfo: [
    { key: 'name', label: 'Contact Name' },
    { key: 'title', label: 'Title' },
    { key: 'email', label: 'Email' },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'breachNotificationEmail', label: 'Breach Notification Email' },
  ],
}

export const aiAssessmentFields = {
  left: [
    { key: 'assessmentTypeToUse', label: 'Assessment Type' },
    { key: 'createdByAi', label: 'Created By' },
  ],
  right: [
    { key: 'createdOn', label: 'Created On' },
    { key: 'website', label: 'Website', isUrl: true },
  ],
}

// Address Form Fields
export const addressFields = [
  {
    key: 'addressTitle',
    label: 'Address Title',
    required: false,
    type: 'input',
    placeholder: 'Enter address title (e.g., Main Office)',
    fullWidth: true,
  },
  {
    key: 'country',
    label: 'Country',
    required: false,
    type: 'select',
    placeholder: 'Select country',
  },
  {
    key: 'state',
    label: 'State',
    required: false,
    type: 'input-select',
    placeholder: 'Enter state/province',
  },
  {
    key: 'city',
    label: 'City',
    required: false,
    type: 'input',
    placeholder: 'Enter city',
  },
  {
    key: 'postalCode',
    label: 'Postal Code',
    required: false,
    type: 'input',
    placeholder: 'Enter postal code',
  },
  {
    key: 'addressLine1',
    label: 'Address Line 1',
    required: false,
    type: 'input',
    placeholder: 'Enter street address',
    fullWidth: true,
  },
  {
    key: 'addressLine2',
    label: 'Address Line 2',
    required: false,
    type: 'input',
    placeholder: 'Enter additional address info',
    fullWidth: true,
  },
]

// Product Fields
export const productInfoFields = [
  { key: 'name', label: 'Product Name' },
  { key: 'createdByAi', label: 'Created By' },
  { key: 'createdOn', label: 'Created On' },
]

export const productStatusFields = [
  {
    key: 'active',
    label: 'Product Status',
    trueText: 'Active',
    falseText: 'Inactive',
  },
  {
    key: 'usesAi',
    label: 'AI Usage',
    trueText: 'Product does use AI',
    falseText: 'Product does not use AI',
  },
]

// ============================================================================
// Options & Utilities
// ============================================================================

// Risk Level Constants
export const riskLevelOptions = [
  { value: 'undetermined', label: 'Undetermined', color: 'grey-7' },
  { value: 'minimal', label: 'Minimal risk product', color: 'success' },
  { value: 'limited', label: 'Limited risk product', color: 'yellow-9' },
  { value: 'high', label: 'High-risk product', color: 'warning' },
  { value: 'unacceptable', label: 'Unacceptable risk product', color: 'black' },
]

export const getRiskLevelDisplay = (riskLevel) => {
  const option = riskLevelOptions.find((opt) => opt.value === riskLevel)
  return option ? option.label : 'Undetermined'
}

export const getRiskLevelColor = (riskLevel) => {
  const option = riskLevelOptions.find((opt) => opt.value === riskLevel)
  return option ? option.color : 'grey-7'
}

export const QUESTION_RISK_LEVEL = {
  HIGH: 'high risk',
  LIMITED: 'limited risk',
  NEGLIGIBLE: 'negligible risk',
}

export const qaRiskLevelOptions = [
  { value: QUESTION_RISK_LEVEL.HIGH, label: 'High risk' },
  { value: QUESTION_RISK_LEVEL.LIMITED, label: 'Limited risk' },
  { value: QUESTION_RISK_LEVEL.NEGLIGIBLE, label: 'Negligible risk' },
]

export const getQaRiskLevelDisplay = (riskLevel) => {
  const option = qaRiskLevelOptions.find((opt) => opt.value === riskLevel)
  return option ? option.label : riskLevel
}

// Company Industries Options
export const COMPANY_INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Manufacturing',
  'Retail',
  'Education',
  'Transportation',
  'Energy',
  'Media',
  'Other',
]

// Employee Range Options
export const employeeRangeOptions = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  '10000+',
]

// ============================================================================
// Field Mapping Functions
// ============================================================================

export function getCompanySectionFieldMap(section) {
  const companySectionMap = {
    info: {
      name: 'company_name',
      legalName: 'company_legal_name',
      active: 'active',
      providesAiServices: 'provides_ai_services',
      parentId: 'parent_id',
      website: 'website',
      isPublicCompany: 'is_public_company',
      stockExchangeName: 'company_stock_exchange_name',
      tickerSymbol: 'company_ticker_symbol',
      numberOfEmployees: 'company_number_of_employees',
    },
    contact: {
      name: 'name',
      title: 'title',
      email: 'email',
      phoneNumber: 'phone_number',
      breachNotificationEmail: 'breach_notification_email',
    },
    aiAssessment: {
      assessmentTypeToUse: 'ai_assessment_to_use',
      website: 'website',
      isTemplate: 'is_template',
      // Q&A sections will be handled separately as they have dynamic structure
    },
  }
  return companySectionMap[section]
}
